import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, ResolveFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { DatabaseService } from '../services/database.service';
import { LoggerService } from '../services/logger.service';
import {
  OrganizationData,
  OrganizationService,
  UserOrganization,
} from '../services/organization.service';
import { UserService } from '../services/user.service';

/**
 * OrganizationResolver - Load user's organizations and handle selection
 *
 * Purpose: Ensure organization context is established before route activates
 *
 * Execution Order: Runs AFTER UserResolver (user is loaded)
 * Success: currentOrganization signal populated, org data available in route.data.org
 * Failure: Redirects to /signup if user has no orgs
 *
 * Flow:
 * 1. Get current user from UserService
 * 2. Load user's organizations list
 * 3. If no orgs: redirect to /signup
 * 4. If orgs exist: Select org (from JWT or first in list)
 * 5. Load selected org data from local database
 *
 * Dependencies:
 * - UserService: Get current user
 * - OrganizationService: Load orgs list and org data
 * - AuthService: Get token and decode JWT
 *
 * Part of authentication flow redesign (Phase 2)
 */
@Injectable({ providedIn: 'root' })
export class OrganizationResolver implements Resolve<OrganizationData | null> {
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private organizationService = inject(OrganizationService);
  private router = inject(Router);
  private logger = inject(LoggerService).getLogger('org-resolver');
  private databaseService = inject(DatabaseService);

  async resolve(route: ActivatedRouteSnapshot): Promise<OrganizationData | null> {
    const resolverStart = performance.now();
    this.logger.info('🔄 START - Loading organization');

    // If the DB is paused (non-leader tab), the overlay is covering the UI.
    // Return null without redirecting — the overlay handles the UX.
    if (this.databaseService.isPaused) {
      this.logger.info('⏸ DB is paused (non-leader tab) — skipping org load');
      return null;
    }

    // CRITICAL: Wait for userService.currentUser() signal to be set by UserResolver
    // Resolvers run in parallel, so we need to wait for UserResolver to complete
    let user = this.userService.currentUser();
    let attempts = 0;
    while (!user && attempts < 50) {
      // Wait 10ms and check again (max 500ms total wait)
      await new Promise((resolve) => setTimeout(resolve, 10));
      user = this.userService.currentUser();
      attempts++;
      this.logger.debug(`⏳ Waiting for user (attempt ${attempts})...`);
    }

    if (!user) {
      this.logger.error('❌ No current user after waiting - UserResolver failed to load user');
      // Check if user has no organization yet (new signup flow)
      // If customer_id is absent from JWT, user hasn't created an org yet
      const token = this.authService.getToken();
      if (token) {
        const payload = this.authService.decodeJwt(token);
        if (payload && !payload.customer_id) {
          this.logger.info('No customer_id in JWT - user has no org, redirecting to signup');
          await this.router.navigate(['/signup']);
          return null;
        }
      }
      // CRITICAL: Redirect to /error, NOT /login
      // User is authenticated (has token) but data integrity is broken
      // Redirecting to /login causes infinite loop with login component effect
      await this.router.navigate(['/error']);
      return null;
    }

    this.logger.debug(`✅ Got user after ${attempts} attempts: ${user.email}`);

    // Get token for loading user's organizations
    const token = this.authService.getToken();
    if (!token) {
      this.logger.error('No authentication token found');
      await this.router.navigate(['/login']);
      return null;
    }

    // Defense-in-depth: Verify currentUser matches JWT identity
    // Prevents stale cached user data from a previous session
    const payload = this.authService.decodeJwt(token);
    if (payload?.sub && payload.sub !== user.id) {
      this.logger.warn(
        `currentUser (${user.email}) does not match JWT sub (${payload.sub}) - stale cache detected`,
      );
      // Wait for UserResolver to load the correct user
      let correctUser = null;
      for (let i = 0; i < 50; i++) {
        await new Promise((resolve) => setTimeout(resolve, 10));
        correctUser = this.userService.currentUser();
        if (correctUser && correctUser.id === payload.sub) {
          break;
        }
        correctUser = null;
      }

      if (!correctUser) {
        this.logger.error('❌ Could not load correct user matching JWT');
        await this.router.navigate(['/error']);
        return null;
      }

      user = correctUser;
      this.logger.debug(`✅ Corrected to user: ${user.email}`);
    }

    try {
      // Load user's organizations list from backend
      const orgs = await this.organizationService.loadUserOrganizations(user.id, token);

      if (!orgs || orgs.length === 0) {
        // User has no organizations - redirect to org creation
        this.logger.info('User has no organizations - redirecting to signup');
        await this.router.navigate(['/signup']);
        return null;
      }

      this.logger.debug(`User has ${orgs.length} organization(s)`);

      // Determine which organization to use
      const selectedOrg = this.selectOrganization(orgs, token);

      if (!selectedOrg) {
        this.logger.error('Failed to select organization');
        await this.router.navigate(['/error']);
        return null;
      }

      // Load organization data from local database
      const orgData = await this.organizationService.loadCurrentOrganization(
        selectedOrg.customer_id,
      );

      if (!orgData) {
        // Organization not in local DB yet - may not have synced
        // This is acceptable - sync will populate it later
        this.logger.warn(
          `Organization ${selectedOrg.customer_id} not in local DB yet - waiting for sync`,
        );
        // Return a minimal org object so route can activate
        return {
          name: selectedOrg.name,
          created_at: '',
          updated_at: '',
        };
      }

      this.logger.debug(`Organization resolved successfully: ${orgData.name}`);
      return orgData;
    } catch (error) {
      this.logger.error('💀 Fatal error in OrganizationResolver:', error);
      // Redirect to error page - likely database failure
      await this.router.navigate(['/error']);
      return null;
    }
  }

  /**
   * Select which organization to use
   * Priority: 1) Org from JWT customer_id, 2) First org in list
   */
  private selectOrganization(orgs: UserOrganization[], token: string): UserOrganization | null {
    // Try to use customer_id from current JWT
    const payload = this.authService.decodeJwt(token);
    const currentCustomerId = payload?.customer_id;

    if (currentCustomerId) {
      const orgFromToken = orgs.find((o) => o.customer_id === currentCustomerId);
      if (orgFromToken) {
        this.logger.debug(`Using organization from JWT: ${orgFromToken.name}`);
        return orgFromToken;
      } else {
        this.logger.warn(
          `JWT customer_id ${currentCustomerId} not in user's orgs - using first org`,
        );
      }
    }

    // Default to first organization
    const firstOrg = orgs[0];
    this.logger.debug(`Using first organization: ${firstOrg.name}`);

    // If JWT doesn't have this org, we should switch to it
    // (This will update the JWT with the correct customer_id)
    if (currentCustomerId !== firstOrg.customer_id) {
      this.logger.info(`Switching to organization: ${firstOrg.customer_id}`);
      // Note: Don't await here - let it happen async
      // The next page load will have the correct JWT
      this.organizationService.switchOrganization(firstOrg.customer_id, token);
    }

    return firstOrg;
  }
}

/**
 * Functional resolver for use in route configuration
 * Delegates to OrganizationResolver class
 */
export const organizationResolver: ResolveFn<OrganizationData | null> = (route) => {
  const resolver = inject(OrganizationResolver);
  return resolver.resolve(route);
};
